import React, { useEffect, useState } from 'react'
import { PRE_CHECK_MACHINE_STATES, STUDENT_PRE_CHECK_STATUS } from '../constants'
import useStudentPreChecks from '../useStudentPreChecks';
import waitForManualPermissionChange from './functions/waitForManualPermissionChange';
import ReloadPageToApplyPermission from './Components/ReloadPageToApplyPermission';
import PromptToAllowPermission from './Components/PromptToAllowPermission';
import hasAudioInputAccess, { getAudioInputAccessState } from './functions/hasAudioInputAccess';
import InstructToFixPermission from './Components/InstructToFixPermission';
import useControlledSpeech from '../Components/hooks/useControlledSpeech';
import { LOCAL_STORAGE_FLAGS } from '../constants';
import { COPY_STRINGS } from '../../../constants/translatedCopy';
import useWindowEvents from '../useWindowEvents';

export const MICROPHONE_STATES = {
    pending: 'pending',
    prompt: 'prompt',
    denied: 'denied',
    granted: 'granted',
    manually_granted: "manually_granted"
}

const HARDWARE_CHECK_EVENTS = {
    MICROPHONE_PROMPT: 'MICROPHONE_PROMPT',
    MICROPHONE_DENIED: 'MICROPHONE_DENIED',
    MICROPHONE_GRANTED: 'MICROPHONE_GRANTED',
    MICROPHONE_MANUALLY_FIXED_GRANTED: 'MICROPHONE_MANUALLY_FIXED_GRANTED',
}

export default React.memo(function HardwareChecks(props) {

    /**
     * props
     */
    const { speechProps } = props

    /**
     * hook
     */
    const { speakWith } = useControlledSpeech({speechProps})
    const studentPreCheck = useStudentPreChecks({itemToCheck: PRE_CHECK_MACHINE_STATES.hardwareChecks})
    const { isCurrent, currentStatus, currentError, settings } = studentPreCheck

    /**
     * states
     */
    const [ permissionState, setPermissionState ] = useState(MICROPHONE_STATES.pending)
    const waitForPermissionChange = waitForManualPermissionChange()

    useWindowEvents()

    useEffect(()=>{
        return () => {
            waitForPermissionChange.stop()
        }
    }, [])

    useEffect(() => {
        if (isCurrent && permissionState === MICROPHONE_STATES.pending) {
            studentPreCheck.startCheck()
            performHardwareChecks()
        }
    }, [isCurrent])

    useEffect(()=> {
        if(permissionState === MICROPHONE_STATES.prompt){
            studentPreCheck.logEvent(HARDWARE_CHECK_EVENTS.MICROPHONE_PROMPT)
        }
        if(permissionState === MICROPHONE_STATES.granted){
            studentPreCheck.logEvent(HARDWARE_CHECK_EVENTS.MICROPHONE_GRANTED)
            waitForPermissionChange.stop()
            finishHardwareChecks()
        }
        if(permissionState === MICROPHONE_STATES.denied){
            studentPreCheck.logEvent(HARDWARE_CHECK_EVENTS.MICROPHONE_DENIED)
            waitForPermissionChange.start(setPermissionState)
        }
        if(permissionState === MICROPHONE_STATES.manually_granted){
            studentPreCheck.logEvent(HARDWARE_CHECK_EVENTS.MICROPHONE_MANUALLY_FIXED_GRANTED)
        }
    }, [permissionState])

    /**
     * methods
     */
    const finishHardwareChecks = () => {
        const handleError = () => setPermissionState(MICROPHONE_STATES.denied)
        const handleSuccess = () => studentPreCheck.finishCheck()
        speechProps.initializeRecorderIfNeeded(handleError, handleSuccess);
    }

    const performHardwareChecks = async () => {

        await speakWith({
            speak: {
                "forReturningUserWithAssessment": COPY_STRINGS.student_checks.intro_assessment_mode
            }
        })

        speechProps.abortDance();


        // micAccessState is only available on browsers supporting permission API
        const micAccessState = await getAudioInputAccessState()
        if(micAccessState){
            if(micAccessState === 'denied'){
                waitForPermissionChange.start(setPermissionState)
                setPermissionState(MICROPHONE_STATES.denied)
            }else if(micAccessState === 'prompt'){
                setPermissionState(MICROPHONE_STATES.prompt)
            }else if(micAccessState === 'granted'){
                setPermissionState(MICROPHONE_STATES.granted)
            }
        }else{
            checkAndRequestPermissionForOtherBrowsers()
        }
    }

    const checkAndRequestPermissionForOtherBrowsers = async () => {
        // if Permission API is not supported, fallback to the older userMedia API
        const hasMicAccess = await hasAudioInputAccess()
        if(hasMicAccess){
            setPermissionState(MICROPHONE_STATES.granted)
        }else{
            setPermissionState(MICROPHONE_STATES.prompt)
        }
    }


    /**
     * start rendering
     */
    if( !studentPreCheck.isCurrent ) return null

    if(permissionState === MICROPHONE_STATES.prompt){
        return <PromptToAllowPermission speechProps={speechProps} setPermissionState={setPermissionState}/>
    }

    if(permissionState === MICROPHONE_STATES.denied){
        return <InstructToFixPermission speechProps={speechProps} toggleReaderContainer={props.toggleReaderContainer}/>
    }

    if(permissionState === MICROPHONE_STATES.manually_granted){
        /**
         * removed when ready module mounted just before it starts the checks, or on beforeunload event.
         * is this reliable? if user refreshed the window
         */
        localStorage.setItem(LOCAL_STORAGE_FLAGS.READY_MODULE_SKIP_FTUE_INTRO, true)

        return <ReloadPageToApplyPermission speechProps={speechProps} toggleReaderContainer={props.toggleReaderContainer}/>
    }

    if(currentStatus === STUDENT_PRE_CHECK_STATUS.failed){

        if(currentError && currentError.includes('Permission denied')){
            return<div>Provide instruction / graphic on how to fix microphone permission.</div>
        }
        return <div>Provide instruction to user to fix permission.</div>
    }
    
    return null
})